import { styled, css } from "styled-components";
import theme from "styled-theming";

import FactBox from "components/FactBox";
import Icon from "components/Icon";
import Link from "components/Link";
import { styledLink } from "components/Link/Link.styled";
import { Container } from "style/components/Page";
import {
    HeadingS,
    styleBodyLAlt,
    styleBodyM,
    styleHeadingXL,
} from "style/components/Typography";
import { columnGap } from "style/grid";
import { MQ } from "style/mediaQueries";

const color = theme("theme", {
    black: "--text-inverted",
    lightgray: "--text-on-neutral-primary",
    blue: "--text-on-blue-primary",
    cyan: "--text-on-cyan-primary",
});

export const OfficeInformation = styled(Container)`
    column-gap: ${columnGap};
    display: grid;
    grid-template-areas:
        "heading"
        "introduction"
        "contactInfo";
    grid-template-columns: repeat(4, [col-start] 1fr);
    padding-bottom: 2.625rem;
    padding-top: 3.5rem;
    position: relative;

    ${MQ.FROM_M} {
        grid-template-columns: repeat(12, [col-start] 1fr);
        padding-bottom: 3.5rem;
        padding-top: 4.375rem;
    }

    ${MQ.FROM_L} {
        grid-template-areas:
            "heading contactInfo"
            "introduction contactInfo";
    }

    ${MQ.FROM_XL} {
        padding-bottom: 4.8125rem;
        padding-top: 6.5625rem;
    }
`;

export const Heading = styled.h1`
    ${styleHeadingXL}
    color: var(${color});
    grid-area: heading;
    grid-column: col-start 1 / span 4;
    margin: 0;

    ${MQ.FROM_M} {
        grid-column: col-start 1 / span 12;
    }

    ${MQ.FROM_L} {
        grid-column: col-start 1 / span 8;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 1 / span 9;
    }
`;

export const Introduction = styled.p`
    ${styleBodyLAlt}
    color: var(${color});
    grid-area: introduction;
    grid-column: col-start 1 / span 4;
    margin-bottom: 0;
    margin-top: 2.625rem;

    ${MQ.FROM_M} {
        grid-column: col-start 2 / span 11;
        margin-top: 3.5rem;
    }

    ${MQ.FROM_L} {
        grid-column: col-start 2 / span 7;
    }
`;

export const ContactInformation = styled(FactBox)`
    && {
        align-self: flex-start;
        grid-area: contactInfo;
        grid-column: col-start 1 / span 4;
        justify-content: flex-start;
        margin-bottom: 0;
        margin-top: 3.0625rem;
        padding-bottom: 2.1875rem;
        row-gap: 3.125rem;

        ${MQ.FROM_M} {
            grid-column: col-start 1 / span 12;
            padding-bottom: 2.625rem;
        }

        ${MQ.FROM_L} {
            grid-column: col-start 9 / span 4;
            margin-top: 0;
        }

        ${MQ.FROM_XL} {
            grid-column: col-start 10 / span 3;
        }
    }
`;

export const ContactInfoDetails = styled.div`
    ${styleBodyM}
    display: flex;
    flex-direction: column;
    row-gap: 2.625rem;
    width: 100%;

    ${MQ.FROM_M} {
        flex-direction: row;
    }

    ${MQ.FROM_L} {
        flex-direction: column;
    }
`;

export const ContactInfoCol = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const ContactInfoDetailItem = styled.div`
    margin-bottom: 2.625rem;
    max-width: 100%;

    &:last-child {
        margin-bottom: 0;
    }
`;

export const DetailHeading = styled(HeadingS)`
    margin-bottom: 0.875rem;
`;

export const FindUsLink = styled(Link)<FindUsLinkProps>`
    ${(props) =>
        props.setTopMargin &&
        css`
            margin-top: 0.875rem;
        `}
`;

export const Email = styled(Link)`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const External = styled(Icon)`
    margin-left: 0.4375rem;
`;

export const StyledPhoneIcon = styled(Icon)`
    && {
        margin-right: 0.4375rem;
        vertical-align: top;
    }
`;

export const OpeLink = styled.span`
    ${styledLink}
`;

interface FindUsLinkProps {
    readonly setTopMargin: boolean;
}
